import { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react'
import { OVERLAY_GROUPS } from '../utils/overlays'
import { ToolIcons } from './toolIcons'
import { TOOL_HOTKEYS } from './toolHotkeys'
import { Flyout, FlyoutItem } from './toolFlyout'
import { ControlIcons, GroupIcons, HIDE_MODE_LABELS, type HideMode } from './drawingBarChrome'
import { PencilIcon } from './PencilIcon'

export type { HideMode }

// Vertical home of the floating drawing bar. The collapsed pencil tab CENTERS
// here (as a % of chart height); the expanded column aligns its TOP to the
// tab's top edge, so it reads as growing DOWN out of the button. The anchor
// sits above the chart's midpoint, which is why the column grows downward:
// that is where the room is. One constant keeps the two branches in lockstep,
// so changing the anchor moves the tab and the column together. 40% read as
// "halfway down" to Chris (2026-09-12); 30% sits visibly in the upper third.
const FLOAT_ANCHOR_TOP_PCT = 30
const PENCIL_TAB_H = 32 // px, matches the tab's h-8
// Breathing room between the column's lowest point and the chart's bottom edge,
// so a tall tool list never runs into the time axis.
const FLOAT_BOTTOM_MARGIN_PX = 8

const caret = (
  <svg width={5} height={5} viewBox="0 0 8 8" className="opacity-70">
    <path d="M0 0h8L4 8z" fill="currentColor" />
  </svg>
)

/* ------------------------------------------------------------------ */
/*  Click-outside hook                                                 */
/* ------------------------------------------------------------------ */

function useClickOutside(ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [ref, onClose])
}

/* ------------------------------------------------------------------ */
/*  DrawingBar                                                         */
/* ------------------------------------------------------------------ */

export interface DrawingBarProps {
  onSelectTool: (overlayName: string) => void
  /**
   * The overlay name of the currently-armed tool (e.g. `'rayLine'`, `'measure'`),
   * or `null` when no tool is armed. Drives the single active-button highlight.
   * The parent clears it on draw-end so the highlight drops once a drawing is
   * placed, and setting it to another tool deactivates the
   * previous one.
   */
  activeTool?: string | null
  onSnapToggle?: () => void
  onLockAll?: () => void
  onTrashAll?: () => void
  snapActive?: boolean
  lockActive?: boolean
  /**
   * Hide/show a category. When provided, the eye button becomes a split control
   * with a menu (drawings / indicators / both) and a main button that repeats
   * the last-used mode. `hiddenState` reflects what's currently hidden.
   */
  onSetHidden?: (mode: HideMode, hidden: boolean) => void
  hiddenState?: { drawings: boolean; indicators: boolean }
  collapsed?: boolean
  onToggleCollapse?: () => void
  /**
   * Ref to the EXPANDED bar's root. Deliberately NOT attached to the collapsed
   * pencil tab, so a host measuring it sees the real column's geometry rather
   * than the tab's — different size, different place.
   */
  rootRef?: (el: HTMLDivElement | null) => void
  /**
   * Floating glass mode (EdgeChart): the bar renders as a backdrop-blurred
   * column absolutely positioned over the chart canvas, and the collapsed
   * state becomes a small pencil tab on the chart's left edge. Hosts that
   * lay the bar beside a cell grid keep the default in-flow layout.
   */
  floating?: boolean
  /**
   * Favorites rail. When provided, the tool section is a flat SHORTLIST of
   * these overlay names (one button each, no group buttons, no hover flyouts)
   * and the full catalog is left to the toolbar's DrawingToolsMenu. Names not
   * in OVERLAY_GROUPS are skipped. An empty list renders a hint in place of
   * the tools; the mode controls below stay. Absent = the grouped catalog.
   */
  favorites?: string[]
}

/** Star outline for the empty-favorites hint. */
const starIcon = (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z" />
  </svg>
)

export function DrawingBar(props: DrawingBarProps) {
  const {
    onSelectTool,
    activeTool,
    onSnapToggle,
    onLockAll,
    onTrashAll,
    snapActive,
    lockActive,
    onSetHidden,
    hiddenState,
    collapsed,
    onToggleCollapse,
    rootRef,
    floating,
    favorites,
  } = props

  const [openMenu, setOpenMenu] = useState<string | null>(null)
  // Per-group last-used tool — drives the persistent button icon.
  const [selectedTools, setSelectedTools] = useState<Record<string, string>>({})
  const [lastHideMode, setLastHideMode] = useState<HideMode>('both')
  const barRef = useRef<HTMLDivElement>(null)

  // The active highlight is parent-controlled via `activeTool`: only the group
  // owning the armed tool lights up, and it clears once the parent reports the
  // draw finished. Choosing a tool in one group therefore deactivates the rest.
  const activeGroupLabel = activeTool
    ? (OVERLAY_GROUPS.find((g) => g.tools.some((t) => t.name === activeTool))?.label ?? null)
    : null

  // Scroll affordance for the tool list. The rail is 36px wide, so a real
  // scrollbar would eat ~40% of it — which is why it is hidden. Hidden, though,
  // meant an overflowing list looked simply truncated: no fade, no hint, no way
  // to know more tools existed. These flags drive edge fades + nudge chevrons so
  // the overflow is visible, while wheel / trackpad scrolling stays the primary
  // interaction.
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollEdges, setScrollEdges] = useState({ up: false, down: false })
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => {
      const max = el.scrollHeight - el.clientHeight
      // 1px slack: fractional layout heights otherwise leave `down` stuck true
      // at the bottom, pinning a chevron that does nothing.
      setScrollEdges({ up: el.scrollTop > 1, down: el.scrollTop < max - 1 })
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    // The list overflows as a function of CHART height, not content — so resize
    // has to re-evaluate, not just scroll. Guarded: jsdom (and older Safari)
    // lack ResizeObserver, and the fades are an affordance, not a dependency —
    // without it they still track scrolling, they just miss pure resizes.
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    ro?.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      ro?.disconnect()
    }
  }, [])
  const nudge = (dir: 1 | -1) => {
    const el = scrollRef.current
    if (el) el.scrollBy({ top: dir * Math.max(60, el.clientHeight * 0.8), behavior: 'smooth' })
  }

  const closeMenu = useCallback(() => setOpenMenu(null), [])
  useClickOutside(barRef, closeMenu)

  if (collapsed) {
    if (floating) {
      // Small pencil tab hugging the chart's left edge, the only trace of the
      // drawing tools until the user clicks it. Solid --primary fill with the
      // glyph in --primary-foreground (the designed contrast pair), so the
      // pencil reads as a dark mark on an accent chip rather than an accent
      // outline on glass. Hover only tints the fill; it never flips back to
      // accent-on-canvas.
      return (
        <button
          data-chart-chrome=""
          title="Expand drawing tools"
          onClick={onToggleCollapse}
          style={{ top: `${FLOAT_ANCHOR_TOP_PCT}%` }}
          className="absolute left-0 z-40 flex h-8 w-5 -translate-y-1/2 items-center justify-center rounded-r-full bg-primary text-primary-foreground opacity-0 shadow-lg shadow-black/40 transition-all chrome-press hover:bg-[hsl(var(--primary)/0.85)] group-hover/chart:opacity-100"
        >
          <PencilIcon size={12} />
        </button>
      )
    }
    return (
      <div
        className="flex w-4 shrink-0 cursor-pointer flex-col items-center justify-center border-r border-border bg-[hsl(var(--chart-canvas))]"
        onClick={onToggleCollapse}
        title="Expand drawing tools"
      >
        <svg
          width={10}
          height={10}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-[hsl(var(--chart-text-muted))]"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    )
  }

  // What "hidden" means for the last-used mode (drives the eye open/closed glyph).
  const isModeHidden = (mode: HideMode): boolean => {
    if (!hiddenState) return false
    if (mode === 'drawings') return hiddenState.drawings
    if (mode === 'indicators') return hiddenState.indicators
    return hiddenState.drawings && hiddenState.indicators
  }
  const lastModeHidden = isModeHidden(lastHideMode)

  return (
    <div
      ref={(el) => {
        barRef.current = el
        rootRef?.(el)
      }}
      // Floating: the panel's TOP aligns to the collapsed pencil tab (centred at
      // FLOAT_ANCHOR_TOP_PCT down), so it appears to grow DOWN out of the button.
      // `top` places the top edge at the tab's top edge; `maxHeight` caps growth
      // at the chart's bottom edge (less FLOAT_BOTTOM_MARGIN_PX) so a tall list
      // scrolls (wheel/trackpad, no scrollbar) while the collapse button below
      // stays pinned and reachable, and a short chart still works.
      style={
        floating
          ? {
              top: `calc(${FLOAT_ANCHOR_TOP_PCT}% - ${PENCIL_TAB_H / 2}px)`,
              maxHeight: `calc(${100 - FLOAT_ANCHOR_TOP_PCT}% + ${PENCIL_TAB_H / 2}px - ${FLOAT_BOTTOM_MARGIN_PX}px)`,
              transformOrigin: 'top center',
              animation: 'edge-drawbar-grow 140ms cubic-bezier(0.16, 1, 0.3, 1)',
            }
          : undefined
      }
      data-chart-chrome=""
      className={
        floating
          ? 'absolute left-0 z-40 flex w-9 flex-col rounded-r-[10px] border border-l-0 border-[hsl(var(--chart-text)/0.15)] bg-[hsl(var(--chart-canvas)/0.85)] py-1 shadow-lg shadow-black/40 backdrop-blur-md'
          : 'flex w-9 shrink-0 flex-col border-r border-border bg-[hsl(var(--chart-canvas))] py-1'
      }
    >
      {/* Floating: the column leads with its close, in the spot the pencil tab
          occupied, so the eye never travels. A muted side chevron, not an
          accent pencil (Chris, 2026-09-12: the close must not be the loudest
          button on screen, and a chevron says "tuck away" more plainly).
          Outside the scroll area so it is never scrolled away. The bottom
          chevron is gone on floating rails: it meant travelling the whole
          rail to close it. */}
      {floating && onToggleCollapse && (
        <div className="flex shrink-0 justify-center pb-0.5">
          <ControlButton title="Hide drawing tools" onClick={onToggleCollapse}>
            <svg
              width={16}
              height={16}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </ControlButton>
        </div>
      )}

      {/* Hide the scroll area's scrollbar cross-browser; wheel/drag still scrolls.
          The grow-down keyframe: a quick fade + slight downward scale from the
          top origin, so the column unfurls out of the pencil tab. */}
      <style>{`.edge-drawing-scroll::-webkit-scrollbar { display: none }
        @keyframes edge-drawbar-grow {
          from { opacity: 0; transform: translateY(-6px) scaleY(0.97); }
          to   { opacity: 1; transform: translateY(0) scaleY(1); }
        }`}</style>
      <div className="relative flex min-h-0 grow flex-col">
        <ScrollNudge dir="up" show={scrollEdges.up} onClick={() => nudge(-1)} />
        <div
          ref={scrollRef}
          className="edge-drawing-scroll flex min-h-0 grow flex-col gap-0.5"
          style={{
            overflowY: 'auto',
            scrollbarWidth: 'none',
            // Stop the scroll chaining to the page (or the chart behind) once the
            // list hits an end — otherwise a trackpad flick over the bar scrolls
            // whatever is underneath it.
            overscrollBehavior: 'contain',
          }}
        >
          {/* Favorites rail: a flat shortlist, one button per favorited tool.
              No groups, no flyouts; the catalog is the toolbar menu's job. */}
          {favorites &&
            (favorites.length === 0 ? (
              <div
                title="No favorites yet. Star tools in the drawing menu to pin them here."
                className="flex h-7 w-8 items-center justify-center self-center text-[hsl(var(--chart-text)/0.4)]"
              >
                {starIcon}
              </div>
            ) : (
              favoriteTools(favorites).map((tool) => (
                <div key={tool.name} className="relative flex justify-center">
                  <ControlButton
                    title={tool.label}
                    active={activeTool === tool.name}
                    activeTone="bg-accent text-[hsl(var(--chart-text)/0.9)]"
                    onClick={() => onSelectTool(tool.name)}
                  >
                    {ToolIcons[tool.name]}
                  </ControlButton>
                </div>
              ))
            ))}

          {/* Drawing tool groups — the button shows the last-used
          tool's icon and re-applies it on click; the caret opens the picker. */}
          {!favorites &&
            OVERLAY_GROUPS.map((group) => {
              const menuKey = `group:${group.label}`
              const isOpen = openMenu === menuKey
              const selectedTool = selectedTools[group.label]
              const buttonIcon = selectedTool
                ? (ToolIcons[selectedTool] ?? GroupIcons[group.label])
                : GroupIcons[group.label]

              return (
                <GroupButton
                  key={group.label}
                  title={selectedTool ? toolLabel(group.label, selectedTool) : group.label}
                  icon={buttonIcon}
                  isOpen={isOpen}
                  active={activeGroupLabel === group.label}
                  onMainClick={() => {
                    // Apply the last-used tool, defaulting to the group's first tool on
                    // the very first click (and remember it so the icon updates).
                    const tool = selectedTool ?? group.tools[0]?.name
                    if (!tool) return
                    onSelectTool(tool)
                    setSelectedTools((prev) => ({ ...prev, [group.label]: tool }))
                    setOpenMenu(null)
                  }}
                  onOpen={() => setOpenMenu(menuKey)}
                  onClose={() => setOpenMenu((m) => (m === menuKey ? null : m))}
                >
                  {isOpen && (
                    <Flyout>
                      {group.tools.map((tool) => (
                        <FlyoutItem
                          key={tool.name}
                          icon={ToolIcons[tool.name]}
                          active={selectedTool === tool.name}
                          hotkey={TOOL_HOTKEYS[tool.name]}
                          onClick={() => {
                            onSelectTool(tool.name)
                            setSelectedTools((prev) => ({ ...prev, [group.label]: tool.name }))
                            setOpenMenu(null)
                          }}
                        >
                          {tool.label}
                        </FlyoutItem>
                      ))}
                    </Flyout>
                  )}
                </GroupButton>
              )
            })}

          {/* Mode controls — inside the scroller, so the bar reads as ONE list
            that scrolls end to end. They were briefly pinned outside it,
            because with the scrollbar hidden an overflowing list gave no hint
            that anything sat below the fold and these simply vanished. The
            edge fades + nudge chevrons now make the overflow visible, so
            scrolling them is discoverable rather than a trap. The collapse
            button below stays pinned — it is the way OUT of a bar that has
            outgrown its space, so it must never need scrolling to reach. */}
          <div className="flex flex-col items-center">
            <div className="mx-2.5 my-0.5 h-px w-full bg-border" />

            {onSnapToggle && (
              <ControlButton
                title={snapActive ? 'Magnet on — snapping to OHLC' : 'Magnet — snap to OHLC'}
                active={snapActive}
                onClick={onSnapToggle}
              >
                {snapActive ? ControlIcons.magnetOn : ControlIcons.magnetOff}
              </ControlButton>
            )}

            {onLockAll && (
              <ControlButton
                title={lockActive ? 'Unlock All' : 'Lock All'}
                active={lockActive}
                onClick={onLockAll}
              >
                {lockActive ? ControlIcons.lock : ControlIcons.unlock}
              </ControlButton>
            )}

            {/* Eye — split control. Main click repeats the last-used hide mode; the
            menu (drawings / indicators / both) opens on hover like the
            drawing-tool groups (caret click still works as the fallback). */}
            {onSetHidden && (
              <GroupButton
                title={`${lastModeHidden ? 'Show' : 'Hide'} ${HIDE_MODE_LABELS[lastHideMode]}`}
                icon={lastModeHidden ? ControlIcons.invisible : ControlIcons.visible}
                isOpen={openMenu === 'hide'}
                active={lastModeHidden}
                onMainClick={() => onSetHidden(lastHideMode, !lastModeHidden)}
                onOpen={() => setOpenMenu('hide')}
                onClose={() => setOpenMenu((m) => (m === 'hide' ? null : m))}
              >
                {openMenu === 'hide' && (
                  <Flyout>
                    {(['drawings', 'indicators', 'both'] as HideMode[]).map((mode) => (
                      <FlyoutItem
                        key={mode}
                        icon={isModeHidden(mode) ? ControlIcons.invisible : ControlIcons.visible}
                        active={mode === lastHideMode}
                        onClick={() => {
                          setLastHideMode(mode)
                          onSetHidden(mode, !isModeHidden(mode))
                          setOpenMenu(null)
                        }}
                      >
                        {isModeHidden(mode) ? 'Show ' : 'Hide '}
                        {HIDE_MODE_LABELS[mode]}
                      </FlyoutItem>
                    ))}
                  </Flyout>
                )}
              </GroupButton>
            )}

            {onTrashAll && (
              <ControlButton title="Remove All" onClick={onTrashAll}>
                {ControlIcons.remove}
              </ControlButton>
            )}
          </div>
        </div>
        <ScrollNudge dir="down" show={scrollEdges.down} onClick={() => nudge(1)} />
      </div>

      {/* In-flow collapse chevron — outside the scroll area so it's always
          reachable, even when a short chart forces the tool list to scroll.
          Floating rails close from the pencil at the top instead. */}
      {!floating && onToggleCollapse && (
        <div className="flex shrink-0 justify-center pb-1 pt-0.5">
          <ControlButton title="Collapse drawing tools" onClick={onToggleCollapse}>
            <svg
              width={16}
              height={16}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </ControlButton>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

/** Resolve favorite names to catalog tools, in the favorites' own order. */
function favoriteTools(names: string[]): Array<{ name: string; label: string }> {
  const out: Array<{ name: string; label: string }> = []
  for (const name of names) {
    for (const group of OVERLAY_GROUPS) {
      const tool = group.tools.find((t) => t.name === name)
      if (tool) {
        out.push({ name: tool.name, label: tool.label })
        break
      }
    }
  }
  return out
}

function toolLabel(groupLabel: string, toolName: string): string {
  const group = OVERLAY_GROUPS.find((g) => g.label === groupLabel)
  return group?.tools.find((t) => t.name === toolName)?.label ?? groupLabel
}

/**
 * A drawing-tool button. HOVER opens the dropdown (after a
 * short delay); a plain CLICK applies the tool. The corner caret is a hover-only
 * hint that also opens the picker immediately if clicked.
 */
function GroupButton({
  title,
  icon,
  isOpen,
  active,
  openOnHover = true,
  onMainClick,
  onOpen,
  onClose,
  children,
}: {
  title: string
  icon: React.ReactNode
  isOpen: boolean
  /** The currently-armed control — the only one that shows the highlight. */
  active?: boolean
  /** When true (default) hovering the button auto-opens its dropdown. */
  openOnHover?: boolean
  onMainClick: () => void
  onOpen: () => void
  onClose: () => void
  children?: React.ReactNode
}) {
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current)
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])
  useEffect(() => clearTimers, [clearTimers])

  const handleEnter = () => {
    if (!openOnHover) return
    if (closeTimer.current) clearTimeout(closeTimer.current)
    openTimer.current = setTimeout(onOpen, 160)
  }
  // The dropdown is a descendant, so moving button → flyout keeps this container
  // "entered"; only leaving the whole thing schedules a (cancellable) close.
  const handleLeave = () => {
    if (!openOnHover) return
    if (openTimer.current) clearTimeout(openTimer.current)
    closeTimer.current = setTimeout(onClose, 160)
  }

  // Named group (`group/tool`) so the caret's group-hover is scoped to THIS
  // button — an unnamed `group` would also match the chart cell wrapper's
  // `group` class, showing the caret when hovering anywhere on the chart.
  const base =
    'group/tool relative flex h-7 w-8 items-center justify-center rounded-sm chrome-press '
  const tone = isOpen
    ? 'bg-accent text-primary'
    : active
      ? 'bg-accent text-[hsl(var(--chart-text)/0.9)] chrome-press hover:bg-accent'
      : 'text-[hsl(var(--chart-text)/0.72)] chrome-press hover:bg-accent hover:text-[hsl(var(--chart-text)/0.9)]'
  return (
    <div
      className="relative flex justify-center"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button title={title} onClick={onMainClick} className={base + tone}>
        {icon}
        {/* Caret corner — hover-only hint; clicking it opens the picker
            immediately (a fallback to the hover-to-open behavior). */}
        <span
          role="button"
          aria-label="More"
          onClick={(e) => {
            e.stopPropagation()
            onOpen()
          }}
          className={
            'absolute bottom-0 right-0 flex h-3 w-3 items-end justify-end p-px text-current transition-opacity group-hover/tool:opacity-100 ' +
            (isOpen ? 'opacity-100' : 'opacity-0')
          }
        >
          {caret}
        </span>
      </button>
      {children}
    </div>
  )
}

function ControlButton({
  children,
  title,
  active,
  activeTone = 'text-primary',
  onClick,
}: {
  children: React.ReactNode
  title?: string
  active?: boolean
  /** Classes for the active look. Mode controls tint the glyph; favorite tool
   *  buttons use the group buttons' filled look so an armed tool reads the
   *  same in both rail modes. */
  activeTone?: string
  onClick?: () => void
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={
        'flex h-7 w-8 items-center justify-center rounded-sm chrome-press ' +
        (active
          ? activeTone + ' chrome-press hover:bg-accent'
          : 'text-[hsl(var(--chart-text)/0.72)] chrome-press hover:bg-accent hover:text-[hsl(var(--chart-text)/0.9)]')
      }
    >
      {children}
    </button>
  )
}

/**
 * Edge fade + chevron marking that the tool list continues past this edge.
 *
 * The fade is the affordance (content visibly runs under it); the chevron is
 * the click target, so the list is reachable with a plain mouse and no wheel.
 * Only the chevron takes pointer events — the fade must not swallow clicks on
 * the tool buttons it sits over.
 */
function ScrollNudge({
  dir,
  show,
  onClick,
}: {
  dir: 'up' | 'down'
  show: boolean
  onClick: () => void
}) {
  if (!show) return null
  const up = dir === 'up'
  return (
    <div
      aria-hidden
      className={
        'pointer-events-none absolute inset-x-0 z-10 flex h-5 justify-center ' +
        (up ? 'top-0 items-start bg-gradient-to-b' : 'bottom-0 items-end bg-gradient-to-t') +
        ' from-[hsl(var(--chart-canvas))] to-transparent'
      }
    >
      <button
        type="button"
        tabIndex={-1}
        title={up ? 'Scroll up' : 'Scroll down'}
        onClick={onClick}
        className="pointer-events-auto flex h-4 w-full items-center justify-center text-[hsl(var(--chart-text)/0.65)] hover:text-primary"
      >
        <svg
          width={11}
          height={11}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points={up ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
        </svg>
      </button>
    </div>
  )
}
